import styled, { css } from 'styled-components';

import { breakpoints } from 'theme';
import {
  BarFill,
  BarTrack,
  PageSubtitle,
  PageTitle,
  SpecPill,
  TrialBanner,
  TrialBannerIcon,
  TrialBannerSub,
  UsageBox,
  UsageBoxSubtitle,
  UsageProBadge,
} from 'pages/ChangePlan/styled';
import {
  BtnPrimary as CancelBtnPrimary,
  BtnSecondary,
} from 'pages/CancelSubscription/styled';

export { BarTrack, BarFill };

export const Eyebrow = styled(UsageBoxSubtitle)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-top: 0;
`;

export const SlotPill = styled(UsageProBadge)<{ $complete: boolean }>`
  letter-spacing: 0.06em;
  padding: 5px 12px;
  ${({ $complete }) =>
    !$complete &&
    css`
      background: #f8faf9;
      color: #999;
      border-color: #e2e8e4;
    `}
`;

export const Panel = styled(UsageBox)`
  width: min(560px, calc(100vw - 32px));
  max-height: min(82vh, 680px);
  display: flex;
  flex-direction: column;
  padding: 0;
  margin-bottom: 0;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(15, 36, 31, 0.12);
`;

export const PanelHeader = styled.div`
  padding: 22px 24px 18px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #f0faf6 0%, #fff 48%);
  border-bottom: 1px solid #e2e8e4;
`;

export const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const TitleIcon = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #fff;
  border: 1.5px solid #e1f5ee;
  font-size: 18px;
  line-height: 1;
`;

export const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Title = styled(PageTitle).attrs({ as: 'h2' })`
  font-size: 21px;
  line-height: 1.25;
  letter-spacing: -0.02em;
  margin: 0;
`;

export const Intro = styled(PageSubtitle)`
  margin: 8px 0 0;
  line-height: 1.55;
`;

/**
 * Full-width amber callout (Change Plan trial banner).
 * Left padding lines note text up with title / intro (`TitleIcon` 40px + gap 12px).
 */
export const OnceNote = styled(TrialBanner)`
  margin-top: 16px;
  margin-bottom: 0;
  padding: 12px 14px 12px 20px;
  gap: 10px;
  align-items: flex-start;
`;

export const OnceNoteIcon = styled(TrialBannerIcon)`
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  color: #a06a0a;
  opacity: 0.85;

  svg {
    display: block;
    width: 18px;
    height: 18px;
  }
`;

export const OnceNoteText = styled(TrialBannerSub)`
  margin-top: 0;
`;

export const ProgressBlock = styled.div`
  margin-top: 18px;
`;

export const SelectionHint = styled(TrialBannerSub)<{ $ready?: boolean }>`
  margin: 10px 0 0;
  font-weight: 600;
  opacity: 1;
  color: ${({ $ready }) => ($ready ? '#00b8a8' : '#555')};
`;

export const ListSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f8faf9;
`;

export const ListHeading = styled(UsageBoxSubtitle)`
  padding: 10px 20px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px 14px 14px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8e4;
    border-radius: 6px;
  }
`;

export const EmptyMessage = styled(PageSubtitle)`
  margin: 0;
  padding: 36px 28px;
  line-height: 1.55;
  text-align: center;
  background: #f8faf9;
`;

export const EmptyIcon = styled.div`
  font-size: 32px;
  line-height: 1;
  margin-bottom: 12px;
`;

export const Row = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  border: ${({ $selected }) =>
    $selected ? '2px solid #00b8a8' : '1.5px solid #e2e8e4'};
  background: #fff;
  margin-bottom: 6px;
  box-shadow: ${({ $selected }) =>
    $selected
      ? '0 2px 10px rgba(0, 184, 168, 0.1)'
      : '0 1px 2px rgba(15, 36, 31, 0.05)'};
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ $selected }) => ($selected ? '#00b8a8' : '#0f241f')};
    box-shadow: 0 4px 14px rgba(15, 36, 31, 0.07);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #00b8a8;
    outline-offset: 2px;
  }
`;

export const AvatarWrap = styled.div<{ $selected: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  background: #f8faf9;
  border: 1.5px solid ${({ $selected }) => ($selected ? '#00b8a8' : '#e2e8e4')};
  box-shadow: ${({ $selected }) => ($selected ? '0 0 0 2px #f0faf6' : 'none')};
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
`;

export const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 50%;
`;

export const InitialsAvatar = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  color: #0f241f;
  background: linear-gradient(145deg, #e1f5ee 0%, #fff 100%);
  border-radius: 50%;
`;

export const RowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RowName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  color: #111;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

/** Active / inactive chip - Change Plan `SpecPill` */
export const StatusPill = SpecPill;

export const SelectionMark = styled.span<{ $selected: boolean }>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: ${({ $selected }) => ($selected ? '#fff' : '#999')};
  background: ${({ $selected }) => ($selected ? '#00b8a8' : '#fff')};
  border: 1.5px solid ${({ $selected }) => ($selected ? '#00b8a8' : '#e2e8e4')};
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
`;

export const Footer = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 20px 20px;
  border-top: 1px solid #e2e8e4;
  justify-content: flex-end;
  flex-wrap: wrap;
  background: #fff;

  @media ${breakpoints.downSmPlus} {
    flex-direction: column;
    align-items: stretch;
  }
`;

/** Cancel subscription flow buttons (same billing palette as Change Plan) */
export const BtnGhost = styled(BtnSecondary)`
  font-weight: 600;

  @media ${breakpoints.downSmPlus} {
    width: 100%;
  }
`;

export const BtnPrimary = styled(CancelBtnPrimary)`
  min-width: 148px;

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    min-width: 0;
  }
`;
